// Lightweight fetch-based API client (mirrors the axios response shape: { data, status }).
// We use fetch instead of axios because XHR requests initiated from user-gesture
// handlers were stalling in this environment; fetch is reliable across all contexts.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth uses an httpOnly session cookie set by the backend (not readable by JS).
// The cookie is sent automatically with same-origin requests via credentials:"include".

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

async function request(method, path, { body, params, isForm = false } = {}) {
  const headers = {};

  let payload;
  if (isForm) {
    payload = body; // FormData; let the browser set the multipart boundary
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  const url = `${API}${path}${buildQuery(params)}`;
  let res;
  try {
    // credentials:"include" sends the httpOnly auth cookie (same-origin API).
    res = await fetch(url, { method, headers, body: payload, credentials: "include" });
  } catch (e) {
    const err = new Error("Network error");
    err.response = undefined;
    throw err;
  }

  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }

  if (!res.ok) {
    if (res.status === 401 && !path.includes("/auth/login")) {
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    const err = new Error((data && data.detail) || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }
  return { data, status: res.status };
}

const get = (path, opts) => request("GET", path, opts);
const post = (path, body, opts = {}) => request("POST", path, { ...opts, body });
const patch = (path, body, opts = {}) => request("PATCH", path, { ...opts, body });
const del = (path, opts) => request("DELETE", path, opts);

// Same-origin download URL. The httpOnly auth cookie is sent automatically by the
// browser for <a href>/window.open GET navigations, so no token is placed in the URL.
const withToken = (path) => `${API}${path}`;

export const api = {
  // Auth & users
  login: (email, password) => post("/auth/login", { email, password }),
  logout: () => post("/auth/logout"),
  me: () => get("/auth/me"),
  changePassword: (current_password, new_password) => post("/auth/change-password", { current_password, new_password }),
  listUsers: () => get("/users"),
  createUser: (payload) => post("/users", payload),
  updateUser: (id, payload) => patch(`/users/${id}`, payload),
  deleteUser: (id) => del(`/users/${id}`),

  uploadDocuments: (files, replace = []) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("files", f));
    (replace || []).forEach((name) => form.append("replace", name));
    return post("/documents/upload", form, { isForm: true });
  },
  getUpload: (id) => get(`/uploads/${id}`),
  listUploads: (activeOnly = false) => get("/uploads", { params: { active_only: activeOnly } }),
  listDocuments: (params = {}) => get("/documents", { params }),
  getDocument: (id) => get(`/documents/${id}`),
  deleteDocument: (id) => del(`/documents/${id}`),
  reprocessDocument: (id) => post(`/documents/${id}/reprocess`),
  documentFileUrl: (id) => withToken(`/documents/${id}/file`),
  listBills: (params = {}) => get("/bills", { params }),
  getBill: (id) => get(`/bills/${id}`),
  updateBill: (id, payload) => patch(`/bills/${id}`, payload),
  markBillReviewed: (id, reviewed = true) => post(`/bills/${id}/review`, { reviewed }),
  paymentsBoard: () => get("/payments/board"),
  markPaid: (billId, confirmationNumber) => post(`/bills/${billId}/pay`, { confirmation_number: confirmationNumber }),
  markUnpaid: (billId) => post(`/bills/${billId}/unpay`),
  expenseLink: (billId) => post(`/bills/${billId}/expense-link`),
  expenseLogged: (billId, payload) => post(`/bills/${billId}/expense-logged`, payload),
  expenseVerify: (billId) => post(`/bills/${billId}/expense-verify`),
  enrichBill: (billId) => post(`/bills/${billId}/enrich`),
  pushRentflow: (billId, { force } = {}) => post(`/bills/${billId}/push-rentflow${force ? "?force=true" : ""}`),
  retryFailedRentflow: () => post("/rentflow/retry-failed"),
  fleetBoard: () => get("/fleet/board"),
  fleetEnrichAll: () => post("/fleet/enrich-all"),
  integrationsStatus: () => get("/integrations/status"),
  listAuthorities: () => get("/authorities"),
  analytics: () => get("/analytics/summary"),
  exportCsvUrl: () => withToken("/export/csv"),
  exportExcelUrl: () => withToken("/export/excel"),
};

// formatting helpers
export const formatCurrency = (v, currency = "USD") => {
  if (v === null || v === undefined || v === "") return "\u2014";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

export const formatDate = (d) => {
  if (!d) return "\u2014";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const formatVal = (v) => (v === null || v === undefined || v === "" ? "\u2014" : String(v));


// A bill is considered "linked to an expense" if the Expenses app has confirmed
// a save. The Expenses app reports this as "created" (new) or "exists"
// (idempotent match), while our backend canonicalizes to "logged". Accept all of
// these (and any record that carries an expense_id) so the UI shows
// "Logged ✓ / Push again" regardless of which status variant was stored.
export const EXPENSE_LINKED_STATUSES = ["logged", "created", "exists", "updated", "saved"];
export const isExpenseLinked = (expensePost) =>
  !!expensePost &&
  (!!expensePost.expense_id ||
    EXPENSE_LINKED_STATUSES.includes(String(expensePost.status || "").toLowerCase()));

// A bill has been successfully pushed to RentFlow when its rentflow_push record
// carries a "pushed" status. An "error" status is surfaced separately.
export const isRentflowPushed = (rentflowPush) =>
  !!rentflowPush && String(rentflowPush.status || "").toLowerCase() === "pushed";
